"use client";

import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { LogOut, Moon, Sun } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useState, useTransition } from "react";

import { signOutLocalAction } from "@/app/app/actions";

import { HEADER_COPY } from "../_lib/copy-shared";
import { ensureSpaceAfterLeadingEmoji, splitLeadingEmoji } from "../_lib/initials";
import { applySupplyLang, pick, type SupplyLang } from "../_lib/lang";
import { applySupplyTheme, type SupplyTheme } from "../_lib/theme";

/**
 * The header's signed-in avatar, promoted from a decorative `aria-hidden`
 * chip to a real menu trigger (2026-08-17 header redesign — operator ask:
 * "move day mode toggle to user button, make it a drop down menu upon hover
 * / click"). Carries the identity the header already resolved
 * (`session.profile.display_name`, threaded down as `displayName` — no new
 * read) and the Day/Night theme control that used to be its own standalone
 * 34px chip (SupplyThemeToggleCompact, now unmounted from the cluster).
 *
 * Built on @base-ui/react's Menu primitives directly rather than the shared
 * components/ui/dropdown-menu.tsx wrapper: that wrapper's classes read
 * `bg-popover`/`hsl(var(--fg-strong))` etc — the OPS vault's token names, not
 * Supply's own `[data-supply]` scope (`var(--raised)`, `var(--muted)`, …) —
 * so reusing it would either render unstyled or fight the two token systems.
 * The primitives give the same keyboard semantics (roving focus, Escape,
 * outside-press dismiss, Enter/Space-to-open on the native <button> the
 * Trigger renders) for free; only the visual styling here is bespoke.
 *
 * openOnHover (Base UI's own MenuTrigger prop, not custom pointer-tracking):
 * "hover-open is acceptable if it does not break touch — click must always
 * work" per the brief. Touch never fires the hover pointer events this reads,
 * so a tap always falls through to the trigger's ordinary click-to-toggle;
 * only a real mouse gets the hover shortcut.
 *
 * Portal container: the popup is portaled to the closest `[data-supply]`
 * ancestor (found via a ref + `closest()`, not `document.body`, the default)
 * so the CSS custom properties this file styles with actually cascade into
 * it — the same fix `components/ui/dropdown-menu.tsx`'s own `container` prop
 * documents for HQ/chef's scoped themes.
 *
 * Theme value: DERIVED during render from the live DOM (`container`'s
 * `data-supply-theme`), never synced via a `useEffect` — React's own
 * guidance is to compute derived state during render rather than mirror it
 * into a second piece of state from an effect (the "effects are for
 * synchronizing with external systems, not for adjusting state" rule; an
 * effect that just calls setState from a render-available value is flagged
 * by eslint-plugin-react-hooks's set-state-in-effect check). `overrideTheme`
 * exists only to make a just-clicked choice show as pressed instantly,
 * without waiting on the DOM mutation to be re-read; once set it wins over
 * the DOM read for the rest of this mount's life, which is always correct
 * here because nothing else ever changes the theme out from under this menu.
 * Reading `document`/the DOM at all is still safe pre-hydration: the Popup
 * subtree is unmounted while closed (Base UI's default), so this only ever
 * runs as a fresh client-only mount, never a reconciliation against server
 * HTML — mirrors why SupplyThemeToggleCompact got away with ZERO React state
 * (pure CSS attribute selectors) for the same reason, one level up.
 *
 * `container` (the closest [data-supply] ancestor, for the portal below) is
 * resolved via a CALLBACK ref rather than `useRef` + `useEffect`: calling
 * setState from a ref callback happens during React's commit phase, not
 * inside an effect body, so it is the React-idiomatic way to react to a DOM
 * node's arrival without tripping the same lint rule.
 *
 * Review round 1 fixes (cold review of the first cut):
 * - The Day/Night control is `MenuPrimitive.RadioGroup`/`RadioItem`, not two
 *   plain `Item`s with a hand-set `aria-pressed` — `aria-pressed` is only
 *   valid on `role="button"`, and Base UI's `Item` renders `role="menuitem"`,
 *   so a screen reader got NO indication of which theme was active. RadioItem
 *   is the primitive built for exactly this (single-select within a menu):
 *   it renders `role="menuitemradio"` + a correct `aria-checked` for free.
 * - Each RadioItem is `min-h-[44px]` (was `h-9` / 36px) — a real touch-floor
 *   violation the existing touch-target-floor.test.ts couldn't see at all,
 *   because it only scans literal `<button` tags in source and Base UI's
 *   MenuItem/RadioItem render a `<div>`. That guard is now extended (see the
 *   test file) to also scan role=menuitem/menuitemradio/option elements and
 *   Base UI Menu(Radio)Item components.
 *
 * 2026-08-20 — SIGN OUT. Supply had no sign-out control anywhere in the app,
 * while hq (SignOutRow), chef (ChefSuiteAccount) and ops (AppShellClient) all
 * had one importing the global `signOutAction`. An oversight, not a decision;
 * operator reported it as "theres no logout button?". It lands HERE rather
 * than as a new screen because this menu is already the account surface, and
 * it is a plain `Item` (an action), not a RadioItem (a selection) — so it is
 * the one legitimate role="menuitem" in a popup that is otherwise all
 * menuitemradio. The ARIA test pins exactly that, so an option regressing to
 * a plain menuitem still fails.
 * Supply deliberately calls `signOutLocalAction`: a shared-device sign-out
 * must never revoke the other active browser sessions.
 *
 * Review round 2 (mobile declutter — operator: "move language toggle to the
 * avatar too — its too crowded"): a second RadioGroup section, Language,
 * below Appearance — same shape, same reason the operator already gave for
 * theme ("they're the same class of setting"). Unlike theme (CSS-only,
 * instant), a language change swaps SERVER-RENDERED TEXT, so picking one
 * calls `router.refresh()` after writing the cookie — same mechanism
 * SupplyLangToggle used (now deleted; that control lived only in this
 * header, and the header no longer renders a language control of its own).
 * `lang` is a normal prop here, not DOM-derived like theme: the page already
 * resolved `await getSupplyLang()` for this exact request and every other
 * consumer (SupplyShell, SupplyTabBar) uses the same value, so RadioGroup's
 * `value={lang}` is authoritative from render one — no "safe because the
 * popup starts unmounted" reasoning required for this section the way theme
 * needed it. "Bahasa Malaysia"/"English" stay LITERAL, not Copy-routed: a
 * language's own name doesn't change when you change the UI language (the
 * old toggle's accessible names, kept verbatim per review round 2).
 *
 * Review round 4 (from a captured screenshot, not DOM measurement — some
 * defects only show up rendered):
 * - The identity row's leading emoji visually crowded the name even with a
 *   real space character in the string (`ensureSpaceAfterLeadingEmoji`) —
 *   emoji glyph metrics eat a following space. Fixed with real layout
 *   (`splitLeadingEmoji` + a flex `gap`) instead of a character; the space-
 *   character helper stays in use for the trigger's `aria-label`, a
 *   pure-text context where a character is the correct fix.
 * - The popup was too narrow for "Skrin terang"/"Bahasa Malaysia" to fit
 *   their `flex-1` column at 224px, so they silently wrapped to two lines
 *   while their shorter partner ("Skrin gelap"/"English") stayed on one —
 *   lopsided pairs, and the reported 44×92 hit area was a two-line box, not
 *   generous padding. Fixed by widening the popup and switching each pair to
 *   an explicit `grid grid-cols-2` (equal columns, not content-sized flex
 *   segments) with `whitespace-nowrap` on every option, so a future longer
 *   string forces the menu wider instead of silently degrading to a wrap.
 *   Re-verified this still fits inside a 320px viewport with no clipping or
 *   horizontal scroll (the binding constraint) before locking the width in.
 */
export function SupplyUserMenu({
  initials,
  displayName,
  lang,
}: {
  initials?: string;
  displayName?: string;
  lang: SupplyLang;
}) {
  const router = useRouter();
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const anchorRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node?.closest<HTMLElement>("[data-supply]") ?? null);
  }, []);
  const [signingOut, startSignOut] = useTransition();
  const [overrideTheme, setOverrideTheme] = useState<SupplyTheme | null>(null);
  const theme: SupplyTheme =
    overrideTheme ?? (container?.dataset.supplyTheme === "dark" ? "dark" : "bright");

  function pickTheme(next: SupplyTheme) {
    applySupplyTheme(container, next);
    setOverrideTheme(next);
  }

  function pickLang(next: SupplyLang) {
    // Mirrors SupplyLangToggle's own no-op guard: re-selecting the already
    // active language should not trigger a refresh.
    if (next === lang) return;
    applySupplyLang(container, next);
    router.refresh();
  }

  // min-h-[44px] is written LITERALLY inside each RadioItem's own className
  // below, not through this shared constant — touch-target-floor.test.ts
  // reads literal Tailwind tokens out of each tag's OWN source text, so a
  // size class that lives one hop away in an interpolated `${sizeConst}`
  // never actually appears in the tag it is scanning (verified live: putting
  // the floor class here, referenced via `${segmentBase}`, left the guard
  // unable to see EITHER the broken 36px or the fixed 44px value — the
  // mutation test below only means something once the class is inline).
  // `active`/`inactive` stay as shared constants because they carry no
  // size information, so their indirection doesn't defeat the scan.
  const active = "bg-[var(--raised)] text-[var(--accent-text)] shadow-[var(--shadow-card)]";
  const inactive = "text-[var(--muted)]";

  return (
    <div ref={anchorRef} className="contents">
      <MenuPrimitive.Root modal={false}>
        <MenuPrimitive.Trigger
          aria-label={
            displayName
              ? `${pick(lang, HEADER_COPY.accountMenu)} — ${ensureSpaceAfterLeadingEmoji(displayName)}`
              : pick(lang, HEADER_COPY.accountMenu)
          }
          title={pick(lang, HEADER_COPY.accountMenu)}
          openOnHover
          delay={120}
          className="relative flex size-[34px] shrink-0 items-center justify-center rounded-[12px] bg-[var(--raised)] text-[12px] font-extrabold text-[var(--accent-text)] transition outline-none active:scale-[0.97] data-popup-open:ring-2 data-popup-open:ring-[var(--accent)]/40 before:absolute before:-inset-[5px] before:content-['']"
        >
          {initials}
        </MenuPrimitive.Trigger>
        <MenuPrimitive.Portal container={container ?? undefined}>
          <MenuPrimitive.Positioner
            className="isolate z-50 outline-none"
            side="bottom"
            align="end"
            sideOffset={8}
          >
            <MenuPrimitive.Popup className="w-[280px] rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-1.5 text-[var(--text)] shadow-[var(--shadow-card)] outline-none">
              {displayName ? (
                <>
                  <div className="px-2.5 py-2">
                    {/* splitLeadingEmoji + a real flex gap, NOT a space
                        character (round 4 — see this file's docblock): the
                        emoji's own glyph metrics swallow a following space
                        regardless of how many are actually in the string. */}
                    <p className="flex items-baseline gap-1.5 truncate text-[13.5px] font-extrabold text-[var(--text)]">
                      {(() => {
                        const { lead, rest } = splitLeadingEmoji(displayName);
                        return (
                          <>
                            {lead ? <span aria-hidden>{lead}</span> : null}
                            <span className="truncate">{rest}</span>
                          </>
                        );
                      })()}
                    </p>
                  </div>
                  <div className="mx-1 mb-1.5 h-px bg-[var(--border-soft)]" />
                </>
              ) : null}
              <div className="px-1.5 pb-1.5">
                <p className="mb-1.5 px-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[var(--faint)]">
                  {pick(lang, HEADER_COPY.appearance)}
                </p>
                {/* MenuRadioGroup/RadioItem, not a plain role="group" of
                    Items with a hand-set aria-pressed — RadioItem renders
                    role="menuitemradio" + a correct aria-checked on its own,
                    which is the ONLY valid way to expose "which one is
                    active" inside a menu (aria-pressed requires
                    role="button"). `value`/`onValueChange` are controlled by
                    the same render-derived `theme` this file already
                    computes — see this file's top docblock for why that read
                    is safe with zero effects. */}
                {/* grid grid-cols-2, not flex + flex-1 (round 4): flex-1
                    columns shrink below their content's natural width when
                    the container is tight, which is exactly how "Skrin
                    terang" silently wrapped to two lines while "Skrin
                    gelap" stayed on one. An explicit 2-col grid + nowrap
                    means a too-long string forces the menu wider (or
                    visibly overflows in dev) instead of quietly degrading
                    to a lopsided wrap. */}
                <MenuPrimitive.RadioGroup
                  aria-label={pick(lang, HEADER_COPY.appearance)}
                  value={theme}
                  onValueChange={(value) => pickTheme(value as SupplyTheme)}
                  className="grid grid-cols-2 gap-1 rounded-[11px] border border-[var(--border)] bg-[var(--inner)] p-1"
                >
                  <MenuPrimitive.RadioItem
                    value="bright"
                    closeOnClick={false}
                    className={`flex min-h-[44px] items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] text-[12.5px] font-bold transition active:scale-[0.98] ${theme === "bright" ? active : inactive}`}
                  >
                    <Sun className="size-[15px]" strokeWidth={2.3} aria-hidden />
                    {pick(lang, HEADER_COPY.dayTheme)}
                  </MenuPrimitive.RadioItem>
                  <MenuPrimitive.RadioItem
                    value="dark"
                    closeOnClick={false}
                    className={`flex min-h-[44px] items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] text-[12.5px] font-bold transition active:scale-[0.98] ${theme === "dark" ? active : inactive}`}
                  >
                    <Moon className="size-[15px]" strokeWidth={2.3} aria-hidden />
                    {pick(lang, HEADER_COPY.nightTheme)}
                  </MenuPrimitive.RadioItem>
                </MenuPrimitive.RadioGroup>
              </div>
              <div className="px-1.5 pb-1.5">
                <p className="mb-1.5 px-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[var(--faint)]">
                  {pick(lang, HEADER_COPY.language)}
                </p>
                <MenuPrimitive.RadioGroup
                  aria-label={pick(lang, HEADER_COPY.language)}
                  value={lang}
                  onValueChange={(value) => pickLang(value as SupplyLang)}
                  className="grid grid-cols-2 gap-1 rounded-[11px] border border-[var(--border)] bg-[var(--inner)] p-1"
                >
                  <MenuPrimitive.RadioItem
                    value="ms"
                    closeOnClick={false}
                    aria-label="Bahasa Malaysia"
                    className={`flex min-h-[44px] items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] text-[12.5px] font-bold transition active:scale-[0.98] ${lang === "ms" ? active : inactive}`}
                  >
                    Bahasa Malaysia
                  </MenuPrimitive.RadioItem>
                  <MenuPrimitive.RadioItem
                    value="en"
                    closeOnClick={false}
                    aria-label="English"
                    className={`flex min-h-[44px] items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] text-[12.5px] font-bold transition active:scale-[0.98] ${lang === "en" ? active : inactive}`}
                  >
                    English
                  </MenuPrimitive.RadioItem>
                </MenuPrimitive.RadioGroup>
              </div>
              {/* Sign out. Last item and visually separated: it is the only
                  destructive-ish action in a menu that is otherwise pure
                  display preferences, and it must not sit adjacent to a
                  RadioItem a thumb is already aiming at.

                  closeOnClick={false} is deliberate and load-bearing. Every
                  other MENU-HOSTED sign-out in this repo (AppShellClient,
                  ChefSuiteAccount, ChefCommandPalette) hoists a hidden form
                  outside the popup and fires it by ref, precisely so the
                  action's owner outlives the click that closes the menu. The
                  inline call is only used where the trigger PERSISTS (hq's
                  SignOutRow, cu's button). Keeping the popup open makes this
                  the persisting-trigger case rather than a fourth pattern —
                  and it is also what makes the pending state reachable at
                  all: with the default close, the item unmounts instantly and
                  neither "Sedang log keluar…" nor the disabled styling could
                  ever paint. */}
              <div className="mx-1 mt-1.5 h-px bg-[var(--border-soft)]" />
              <div className="px-1.5 pb-0.5 pt-1.5">
                <MenuPrimitive.Item
                  closeOnClick={false}
                  disabled={signingOut}
                  // AWAITED, not `void`-discarded: React 19 keeps isPending
                  // true for the lifetime of an async transition callback, so
                  // this is what actually makes `signingOut` track the request.
                  // With `void` the transition settles on the same tick and
                  // the pending copy never paints, closeOnClick or not.
                  onClick={() => startSignOut(async () => {
                    // try/catch, not a bare await: an unhandled throw inside a
                    // transition escalates to the nearest error boundary
                    // (app/supply/error.tsx), so a transient network blip would
                    // replace the whole screen with an error page for what is a
                    // retryable tap. Swallowing it just re-enables the item.
                    // The hq/cu precedent uses `void action()`, which cannot
                    // throw here but also cannot power a pending state.
                    try { await signOutLocalAction(); } catch { /* re-enable and let them retry */ }
                  })}
                  className="flex min-h-[44px] w-full cursor-pointer items-center gap-2 rounded-[11px] px-2.5 text-[13px] font-bold text-[var(--text)] outline-none transition select-none data-[highlighted]:bg-[var(--inner)] data-[disabled]:opacity-60 active:scale-[0.99]"
                >
                  <LogOut className="size-[15px]" strokeWidth={2.3} aria-hidden />
                  {pick(lang, signingOut ? HEADER_COPY.signingOut : HEADER_COPY.signOut)}
                </MenuPrimitive.Item>
              </div>
            </MenuPrimitive.Popup>
          </MenuPrimitive.Positioner>
        </MenuPrimitive.Portal>
      </MenuPrimitive.Root>
    </div>
  );
}
